import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, FileText, Trash2, TrendingUp, TrendingDown, CalendarClock } from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import './NotesTasksScreen.css';

interface StockNote {
  id: number;
  symbole: string;
  content: string;
  snapshot_cours: number | null;
  created_at: string;
  updated_at: string;
}

interface StockTask {
  id: number;
  symbole: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  created_at: string;
  completed_at: string | null;
}

interface LatestPrice {
  cours: number;
  date: string;
}

type TaskFilter = 'ACTIVE' | 'TODO' | 'IN_PROGRESS' | 'DONE' | 'ALL';

const TASK_FILTERS: { value: TaskFilter; label: string }[] = [
  { value: 'ACTIVE', label: 'À suivre' },
  { value: 'TODO', label: 'À faire' },
  { value: 'IN_PROGRESS', label: 'En cours' },
  { value: 'DONE', label: 'Terminées' },
  { value: 'ALL', label: 'Toutes' },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'TODO', label: 'À faire' },
  { value: 'IN_PROGRESS', label: 'En cours' },
  { value: 'DONE', label: 'Terminée' },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'DONE': return '#16a34a';
    case 'IN_PROGRESS': return '#f59e0b';
    default: return '#6b7280';
  }
};

const StockAvatar: React.FC<{ symbole: string; broken: boolean; onError: () => void }> = ({ symbole, broken, onError }) => (
  broken ? (
    <div className='notes-tasks-avatar notes-tasks-avatar-fallback'>{symbole.slice(0, 4)}</div>
  ) : (
    <img
      src={`/images/societes/${symbole}.png`}
      alt={symbole}
      onError={onError}
      className='notes-tasks-avatar'
    />
  )
);

const NotesTasksScreen: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<'tasks' | 'notes'>('tasks');
  const [notes, setNotes] = useState<StockNote[]>([]);
  const [tasks, setTasks] = useState<StockTask[]>([]);
  const [taskFilter, setTaskFilter] = useState<TaskFilter>('ACTIVE');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [priceMap, setPriceMap] = useState<Record<string, LatestPrice>>({});
  const [imgErrorMap, setImgErrorMap] = useState<Record<string, boolean>>({});

  const handleImgError = (symbole: string) => {
    setImgErrorMap(prev => ({ ...prev, [symbole]: true }));
  };

  useEffect(() => {
    if (!token) return;
    fetchAll();
  }, [token]);

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [notesRes, tasksRes] = await Promise.all([
        fetch(`${API_URL}/v1/notes`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/v1/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!notesRes.ok || !tasksRes.ok) throw new Error('Échec du chargement des notes/tâches');
      setNotes(await notesRes.json());
      setTasks(await tasksRes.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const uniqueSymbols = useMemo(() => {
    const set = new Set<string>();
    notes.forEach(n => set.add(n.symbole));
    tasks.forEach(t => set.add(t.symbole));
    return Array.from(set).sort();
  }, [notes, tasks]);
  const uniqueSymbolsKey = uniqueSymbols.join(',');

  useEffect(() => {
    if (!token || uniqueSymbols.length === 0) return;
    let cancelled = false;
    Promise.all(
      uniqueSymbols.map(async (sym) => {
        try {
          const res = await fetch(`${API_URL}/v1/stocks/${sym}/history?limit=1`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.length > 0 && data[0].cours_cloture != null) {
              return [sym, { cours: data[0].cours_cloture, date: data[0].date_cotation }] as const;
            }
          }
        } catch (err) {
          console.error(`Error fetching price for ${sym}:`, err);
        }
        return null;
      })
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, LatestPrice> = {};
      results.forEach((r) => {
        if (r) map[r[0]] = r[1];
      });
      setPriceMap(map);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueSymbolsKey, token]);

  const handleChangeTaskStatus = async (task: StockTask, newStatus: string) => {
    if (newStatus === task.status) return;
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, status: newStatus } : t)));
    try {
      await fetch(`${API_URL}/v1/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (err) {
      console.error('Error updating task status:', err);
      fetchAll();
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    setTasks(prev => prev.filter(t => t.id !== taskId));
    try {
      await fetch(`${API_URL}/v1/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Error deleting task:', err);
      fetchAll();
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    setNotes(prev => prev.filter(n => n.id !== noteId));
    try {
      await fetch(`${API_URL}/v1/notes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Error deleting note:', err);
      fetchAll();
    }
  };

  const filteredTasks = useMemo(() => {
    if (taskFilter === 'ALL') return tasks;
    if (taskFilter === 'ACTIVE') return tasks.filter(t => t.status !== 'DONE');
    return tasks.filter(t => t.status === taskFilter);
  }, [tasks, taskFilter]);

  const overdueCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return tasks.filter(t => t.status !== 'DONE' && t.due_date && t.due_date < today).length;
  }, [tasks]);

  const activeTasksCount = tasks.filter(t => t.status !== 'DONE').length;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return '#dc2626';
      case 'LOW': return '#6b7280';
      default: return '#f59e0b';
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

  const isOverdue = (task: StockTask) => {
    if (task.status === 'DONE' || !task.due_date) return false;
    return task.due_date < new Date().toISOString().slice(0, 10);
  };

  const getVariation = (symbole: string, snapshot: number | null) => {
    const current = priceMap[symbole];
    if (!current || snapshot == null || snapshot === 0) return null;
    const pct = ((current.cours - snapshot) / snapshot) * 100;
    return { pct, current: current.cours };
  };

  if (loading) {
    return (
      <SiteLayout>
        <Header icon='assignment' title='Notes & Tâches' />
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>Chargement...</p>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Header icon='assignment' title='Notes & Tâches' />
      <div className='notes-tasks-container'>
        {error && (
          <Box>
            <div className='box-content box-horizontal-padding' style={{ padding: '20px', color: '#f87171' }}>
              {error}
            </div>
          </Box>
        )}

        <div className='notes-tasks-tabs'>
          <button
            className={`notes-tasks-tab ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
          >
            <ClipboardList size={16} />
            Tâches
            {activeTasksCount > 0 && <span className='notes-tasks-badge'>{activeTasksCount}</span>}
            {overdueCount > 0 && <span className='notes-tasks-badge overdue'>{overdueCount} en retard</span>}
          </button>
          <button
            className={`notes-tasks-tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            <FileText size={16} />
            Notes
            {notes.length > 0 && <span className='notes-tasks-badge'>{notes.length}</span>}
          </button>
        </div>

        {activeTab === 'tasks' ? (
          <Box>
            <div className='box-title box-vertical-padding box-horizontal-padding'>
              <div className='notes-tasks-filters'>
                {TASK_FILTERS.map(f => (
                  <button
                    key={f.value}
                    className={`notes-tasks-filter-btn ${taskFilter === f.value ? 'active' : ''}`}
                    onClick={() => setTaskFilter(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className='notes-tasks-list box-horizontal-padding box-vertical-padding'>
              {filteredTasks.length === 0 ? (
                <div className='notes-tasks-empty'>
                  <ClipboardList size={36} strokeWidth={1.5} />
                  <p>Aucune tâche{taskFilter !== 'ALL' ? ' dans ce filtre' : ''}</p>
                  {/* Une tâche est toujours rattachée à une valeur : elle se crée depuis sa fiche. */}
                  <p style={{ fontSize: '0.85rem', maxWidth: 420, margin: '8px auto 14px' }}>
                    Les tâches et les notes se créent depuis la fiche d'une valeur, avec le bouton « Notes » en bas à droite de l'écran.
                  </p>
                  <Link to='/data' className='button button-purple button-medium'>Choisir une valeur</Link>
                </div>
              ) : (
                filteredTasks.map(task => (
                  <div key={task.id} className={`notes-tasks-task-row ${task.status === 'DONE' ? 'done' : ''} ${isOverdue(task) ? 'overdue' : ''}`}>
                    <select
                      className='notes-tasks-status-select'
                      value={task.status}
                      onChange={(e) => handleChangeTaskStatus(task, e.target.value)}
                      style={{ color: getStatusColor(task.status), borderColor: getStatusColor(task.status) }}
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <Link to={`/data?symbol=${task.symbole}`}>
                      <StockAvatar symbole={task.symbole} broken={!!imgErrorMap[task.symbole]} onError={() => handleImgError(task.symbole)} />
                    </Link>
                    <div className='notes-tasks-task-main'>
                      <div className='notes-tasks-task-title'>{task.title}</div>
                      <div className='notes-tasks-task-meta'>
                        <Link to={`/data?symbol=${task.symbole}`} className='notes-tasks-symbol'>
                          {task.symbole}
                        </Link>
                        <span className='notes-tasks-priority-chip' style={{ color: getPriorityColor(task.priority) }}>
                          <span className='notes-tasks-priority-dot' style={{ background: getPriorityColor(task.priority) }} />
                          {task.priority}
                        </span>
                        {task.due_date && (
                          <span className={`notes-tasks-due ${isOverdue(task) ? 'notes-tasks-overdue-text' : ''}`}>
                            <CalendarClock size={12} /> {formatDate(task.due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button className='notes-tasks-delete-btn' onClick={() => handleDeleteTask(task.id)} title='Supprimer'>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </Box>
        ) : (
          <>
            {notes.length === 0 ? (
              <Box>
                <div className='notes-tasks-empty' style={{ padding: '40px 0' }}>
                  <FileText size={36} strokeWidth={1.5} />
                  <p>Aucune note pour le moment</p>
                </div>
              </Box>
            ) : (
              <div className='notes-tasks-notes-grid'>
                {notes.map(note => {
                  const variation = getVariation(note.symbole, note.snapshot_cours);
                  return (
                    <div key={note.id} className='notes-tasks-note-card'>
                      <div className='notes-tasks-note-header'>
                        <Link to={`/data?symbol=${note.symbole}`} className='notes-tasks-note-header-link'>
                          <StockAvatar symbole={note.symbole} broken={!!imgErrorMap[note.symbole]} onError={() => handleImgError(note.symbole)} />
                          <span className='notes-tasks-symbol'>{note.symbole}</span>
                        </Link>
                        <button className='notes-tasks-delete-btn' onClick={() => handleDeleteNote(note.id)} title='Supprimer'>
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <p className='notes-tasks-note-content'>{note.content}</p>
                      <div className='notes-tasks-note-footer'>
                        <span className='notes-tasks-note-date'>{formatDate(note.updated_at)}</span>
                        {note.snapshot_cours != null && (
                          <div className='notes-tasks-price-evolution'>
                            <span>Cours à la note : <strong>{note.snapshot_cours.toLocaleString('fr-FR')} F</strong></span>
                            {variation ? (
                              <span className={`notes-tasks-variation-badge ${variation.pct >= 0 ? 'positive' : 'negative'}`}>
                                {variation.pct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                {variation.pct >= 0 ? '+' : ''}{variation.pct.toFixed(1)}%
                                <span className='notes-tasks-current-price'>· {variation.current.toLocaleString('fr-FR')} F auj.</span>
                              </span>
                            ) : (
                              <span className='notes-tasks-variation-badge neutral'>Cours actuel indisponible</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
};

export default NotesTasksScreen;
